export const MOIS_LABELS = [
  "Janvier","Février","Mars","Avril","Mai","Juin",
  "Juillet","Août","Septembre","Octobre","Novembre","Décembre",
];

export const QUARTER_MONTHS: Record<number, [number, number, number]> = {
  1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12],
};

export const HEURES_LABELS = Array.from({length:24},(_,h)=>`${String(h).padStart(2,"0")}:00`);
export const JOURS_SEMAINE = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];

/**
 * Détecte les jours manquants dans une série temporelle journalière.
 * Un jour est considéré manquant si l'écart entre deux points consécutifs
 * dépasse 1 jour (tolérance DST via Math.round).
 *
 * @param sorted Points triés par timestamp croissant
 * @returns Labels lisibles des jours manquants, ex. ["3 Mai", "4 Mai"]
 */
export function findMissingDays(sorted: { t: number }[]): string[] {
  const MS_DAY = 86_400_000;
  const missing: string[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const gapDays = Math.round((sorted[i].t - sorted[i - 1].t) / MS_DAY);
    for (let d = 1; d < gapDays; d++) {
      const dt = new Date(sorted[i - 1].t + d * MS_DAY);
      missing.push(`${dt.getDate()} ${MOIS_LABELS[dt.getMonth()]}`);
    }
  }
  return missing;
}

/**
 * Regroupe une liste triée de labels "D Mois" en plages compactes.
 * Ex: ["20 Juin","21 Juin","22 Juin","25 Juin"] → "20–22 Juin, 25 Juin"
 * Ex: ["30 Juin","1 Juillet"] → "30 Juin – 1 Juillet"
 */
export function formatMissingDaysCompact(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];

  const parsed = labels.map(label => {
    const sp = label.indexOf(" ");
    return { day: parseInt(label.slice(0, sp), 10), monthIdx: MOIS_LABELS.indexOf(label.slice(sp + 1)), label };
  });

  const groups: { start: typeof parsed[0]; end: typeof parsed[0] }[] = [];
  let groupStart = parsed[0];
  let groupEnd   = parsed[0];

  for (let i = 1; i < parsed.length; i++) {
    const prev = parsed[i - 1];
    const curr = parsed[i];
    const consecutive =
      (prev.monthIdx === curr.monthIdx && curr.day === prev.day + 1) ||
      (curr.monthIdx === prev.monthIdx + 1 && curr.day === 1);
    if (consecutive) {
      groupEnd = curr;
    } else {
      groups.push({ start: groupStart, end: groupEnd });
      groupStart = curr;
      groupEnd   = curr;
    }
  }
  groups.push({ start: groupStart, end: groupEnd });

  return groups.map(({ start, end }) => {
    if (start.label === end.label) return start.label;
    if (start.monthIdx === end.monthIdx) return `${start.day}–${end.label}`;
    return `${start.label} – ${end.label}`;
  }).join(", ");
}

/** "YYYY-MM-DD" → "D Mois" (ex: "2024-01-03" → "3 Janvier") */
export function formatIsoDay(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${parseInt(m[3])} ${MOIS_LABELS[parseInt(m[2]) - 1]}` : iso;
}

/**
 * Regroupe une liste triée de numéros de mois (1–12) en sous-listes consécutives.
 * Ex: [1,2,3,6] → [[1,2,3],[6]]
 */
export function groupConsecMonths(months: number[]): number[][] {
  if (!months.length) return [];
  const groups: number[][] = [[months[0]]];
  for (let i = 1; i < months.length; i++) {
    if (months[i] === months[i - 1] + 1) groups[groups.length - 1].push(months[i]);
    else groups.push([months[i]]);
  }
  return groups;
}

/** Formate un groupe de mois consécutifs en "Jan–Mar 2024" ou "Janvier 2024".
 *  Si `annee` est omis, retourne "Janvier–Mars" sans année. */
export function fmtMonthRange(group: number[], annee?: number): string {
  const s = group[0], e = group[group.length - 1];
  const suffix = annee != null ? ` ${annee}` : "";
  return group.length === 1
    ? `${MOIS_LABELS[s - 1]}${suffix}`
    : `${MOIS_LABELS[s - 1]}–${MOIS_LABELS[e - 1]}${suffix}`;
}

export function missingDaysForPeriod(sorted: { t: number }[], period: string): string[] {
  const mm = period.match(/^(\d{4})-(\d{2})$/);
  const qt = period.match(/^(\d{4})-T([1-4])$/);
  const yr = period.match(/^(\d{4})$/);
  let start: Date, end: Date;
  if (mm) {
    const [, y, m] = mm;
    start = new Date(+y, +m - 1, 1);
    end   = new Date(+y, +m, 0);
  } else if (qt) {
    const [, y, q] = qt;
    const m0 = (+q - 1) * 3;
    start = new Date(+y, m0, 1);
    end   = new Date(+y, m0 + 3, 0);
  } else if (yr) {
    start = new Date(+yr[1], 0, 1);
    end   = new Date(+yr[1], 11, 31);
  } else {
    return findMissingDays(sorted);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (end > today) end = today;

  const present = new Set(sorted.map(p => {
    const d = new Date(p.t);
    return `${d.getFullYear()}/${d.getMonth()}/${d.getDate()}`;
  }));
  const missing: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    if (!present.has(`${cur.getFullYear()}/${cur.getMonth()}/${cur.getDate()}`)) {
      missing.push(`${cur.getDate()} ${MOIS_LABELS[cur.getMonth()]}`);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return missing;
}
